import React, { useState, useEffect } from "react";
import styled from "styled-components";
import AOS from "aos";
const faqData = [
  {
    question: "What is GetThru?",
    answer:
      "GetThru is a high-quality, clinician-led medical exam question bank built for doctors preparing for a wide range of specialty and general medical exams. With expertly written questions and rich explanations, GetThru helps you study smarter, build confidence, and succeed in high-stakes assessments."
  },
  {
    question: "Who writes the questions?",
    answer:
      "All content is created by experienced clinicians, medical educators, and recent alumni in the relevant exams. The questions in our question banks are designed to reflect real exam formats and current clinical knowledge, so you're always practicing what's most relevant."
  },
  {
    question: "How is GetThru different from other question banks?",
    answer:
      "Unlike many generic question banks, GetThru is built with insight from those who’ve sat the exams you're facing. We focus on clarity, depth, and real-world applicability, helping you actively learn rather than just memorise. With intuitive design, analytics, and a focus on user experience, GetThru makes studying efficient and effective."
  },
  {
    question: "How should I use GetThru?",
    answer:
      "Use GetThru early and often during your study journey. Start with untimed practice sessions to build foundational knowledge, then switch to exam-style timed questions to refine technique. Our exam question banks are designed to adapt to your needs — from early prep to final review."
  },
  {
    question: "How many practice questions should I aim to complete each day?",
    answer:
      "It depends on your timeline, but 10–30 well-reviewed questions per day is ideal. The key is consistency. GetThru tracks your accuracy, highlights weak areas, and allows you to revisit flagged questions — helping you maximise your study time"
  },
  {
    question: "Do I still need to read textbooks?",
    answer:
      "Yes — while GetThru is a powerful learning tool, it’s best used alongside clinical textbooks and guidelines. Our explanations are detailed and educational, but for exam success and clinical reasoning, a combination of question-based and traditional study is most effective."
  },
  {
    question: "Is there a free trial?",
    answer:
      "Yes, we offer a free access to our question banks to get you started so you can try before you commit. See the platform, experience our explanations, and decide if GetThru fits your study approach. Sign up for premium to get access to a large and growing bank of questions and full finality to boost your study"
  }
];

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    // Initialize AOS with settings to only animate once
    AOS.init({
      once: true, // This is the key setting - animations will only happen once
      mirror: false, // Don't mirror animations when scrolling back up
      duration: 800,
      offset: 100, // Controls when the animation triggers
      easing: "ease-out"
    });

    // Refresh AOS on component mount
    AOS.refresh();
  }, []);

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <Container>
      <LeftSection data-aos="fade-right" data-aos-duration="1000">
        <Title>Frequently asked questions (FAQ)</Title>
        <FaqImage
          src="/assets/home/faq.png"
          alt="faq"
          width={154}
          height={152}
        />
      </LeftSection>
      <RightSection data-aos="fade-left" data-aos-duration="1000">
        {faqData.map((item, index) => (
          <AccordionItem
            key={index}
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay={50 * index}
          >
            <QuestionButton
              isActive={activeIndex === index}
              onClick={() => toggleAccordion(index)}
            >
              <Arrow isActive={activeIndex === index} />
              {item.question}
            </QuestionButton>
            <Answer isOpen={activeIndex === index}>{item.answer}</Answer>
          </AccordionItem>
        ))}
      </RightSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 24px 16px;
  }
`;

const LeftSection = styled.div`
  flex: 0 0 35%;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const RightSection = styled.div`
  flex: 0 0 58%;
`;

const Title = styled.h2`
  font-size: 32px;
  margin-bottom: 130px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 0px;
  }
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const QuestionButton = styled.button<{ isActive: boolean }>`
  width: 100%;
  text-align: left;
  padding: 16px 0;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: 24px;
  color: ${props => (props.isActive ? "#000" : "#333")};
  font-weight: ${props => (props.isActive ? "600" : "400")};
  gap: 8px;
  font-weight: 700;

  @media (max-width: 768px) {
    padding: 12px 0;
    font-size: 18px;
  }

  &:hover {
    color: #1c1c1f;
  }
`;

const Answer = styled.div<{ isOpen: boolean }>`
  visibility: ${props => (props.isOpen ? "visible" : "hidden")};
  opacity: ${props => (props.isOpen ? 1 : 0)};
  height: ${props => (props.isOpen ? "auto" : "0")};
  transform: ${props => (props.isOpen ? "translateY(0)" : "translateY(-8px)")};
  transition: opacity 0.4s ease-out, transform 0.4s ease-out,
    visibility 0s linear ${props => (props.isOpen ? "0s" : "0.4s")};
  will-change: opacity, transform;
  padding: ${props => (props.isOpen ? "0 0 16px 32px" : "0")};
  font-size: 18px;
  color: #1c1c1f;

  @media (max-width: 768px) {
    padding: ${props => (props.isOpen ? "0 0 12px 24px" : "0")};
    font-size: 16px;
  }
`;

const Arrow = styled.span<{ isActive: boolean }>`
  transform: ${props => (props.isActive ? "rotate(90deg)" : "rotate(0)")};
  transition: transform 0.4s ease-out;
  will-change: transform;
  &::before {
    content: "→";
  }
`;

const FaqImage = styled.img`
  width: 154px;
  height: auto;
  object-fit: contain;
  color: #e7eeff;
  @media (max-width: 768px) {
    display: none;
  }
`;

export default FAQ;
